import React, { useEffect, useState, useMemo } from "react";
import { ConfigProvider, Skeleton, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table/interface";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const UserDeviceTableComponent = () => {
  const { Title } = Typography;

  const [versionData, setVersionData] = useState({
    standard_latest: "",
    standard_allowed: [],
    stealth_allowed: [],
    stealth_latest: "",
  });

  const {
    health_status_device_info_processing,
    health_status_device_info_data,
    health_status_device_info_success,
  } = useSelector(
    // @ts-ignore
    (state) => state.userDevice
  );

  const { userData, userDataLoading } = useSelector(
    // @ts-ignore
    (state) => state.livestream
  );

  const [devcieData, setDevcieData] = useState([]);

  const mergeData = (rawdata, userdata) => {
    return rawdata.map((entry) => {
      const match = userdata?.[entry.iden_id];
      return match
        ? { ...entry, timestamp: match.timestamp, localDate: match.localDate }
        : entry;
    });
  };

  useEffect(() => {
    const fetchVersionData = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_CONTROL_CENTER_ENDPOINT +
            "discover/app_versions_v2"
        );
        const data = await response.json();
        setVersionData(data);
      } catch (err) {
        console.error("Version API failed", err);
      }
    };
    fetchVersionData();
  }, []);

  useEffect(() => {
    if (
      health_status_device_info_success &&
      health_status_device_info_data?.length > 0
    ) {
      const rowData = [];

      health_status_device_info_data.forEach((item) => {
        if (item.devices.length > 1) {
          rowData.push({
            key: item.iden_id, // ✅ parent key
            iden_id: item.iden_id,
            first_name: `${item.first_name} ${item.last_name}`,
            team_name: item.team_name,
            employee_id: item.employee_id,

            device_name: item.devices[0].device_name,
            device_id: item.devices[0].device_id,
            platform: item.devices[0].platform,
            os_name: item.devices[0].os_name,
            os_version: item.devices[0].os_version,
            cpu_arch: item.devices[0].cpu_arch,
            ip: item.devices[0].ip,
            myzen_version: item.devices[0].myzen_version,
            version_type: item.devices[0].version_type,

            children: item.devices.slice(1).map((d) => ({
              key: d.device_id, // ✅ UNIQUE child key
              iden_id: d.iden_id,
              first_name: item.first_name,
              team_name: item.team_name,
              employee_id: item.employee_id,

              device_name: d.device_name,
              device_id: d.device_id,
              platform: d.platform,
              os_name: d.os_name,
              os_version: d.os_version,
              cpu_arch: d.cpu_arch,
              ip: d.ip,
              myzen_version: d.myzen_version,
              version_type: d.version_type,
            })),
          });
        } else {
          rowData.push({
            key: item.iden_id,
            iden_id: item.iden_id,
            first_name: `${item.first_name} ${item.last_name}`,
            team_name: item.team_name,
            employee_id: item.employee_id,

            device_name: item.devices[0].device_name,
            device_id: item.devices[0].device_id,
            platform: item.devices[0].platform,
            os_name: item.devices[0].os_name,
            os_version: item.devices[0].os_version,
            cpu_arch: item.devices[0].cpu_arch,
            ip: item.devices[0].ip,
            myzen_version: item.devices[0].myzen_version,
            version_type: item.devices[0].version_type,
          });
        }
      });

      setDevcieData(
        Object.keys(userData || {}).length > 0
          ? mergeData(rowData, userData)
          : rowData
      );
    } else {
      setDevcieData([]);
    }
  }, [health_status_device_info_success, userDataLoading]);

  const expandedRowKeys = useMemo(
    () =>
      devcieData
        .filter((row) => row.children && row.children.length > 0)
        .map((row) => row.key),
    [devcieData]
  );

  const columns: ColumnsType<HealthlyUserDeviceInfo> = [
    {
      title: "Machine User Name",
      dataIndex: "device_name",
      width: 180,
    },
    {
      title: "MyZen Version",
      dataIndex: "myzen_version",
      width: 140,
      render: (val) => val?.match(/^\d+\.\d+\.\d+/)?.[0] || "",
    },
    {
      title: "App Type",
      dataIndex: "version_type",
      width: 130,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      width: 120,
    },
    {
      title: "OS Name",
      dataIndex: "os_name",
      width: 180,
    },
    {
      title: "OS Build",
      dataIndex: "os_version",
      width: 150,
      ellipsis: true,
    },
    {
      title: "System Type",
      dataIndex: "cpu_arch",
      width: 130,
    },
    {
      title: "IP",
      dataIndex: "ip",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Last Ping Since",
      dataIndex: "timestamp",
      width: 160,
      render: (val) => (val ? dayjs(val).fromNow() : ""),
    },
    {
      title: "Version Status",
      dataIndex: "myzen_version",
      width: 200,
      render: (val, record) => {
        if (!val) return <Tag>Unknown</Tag>;

        const version = val.split(" ")[0];
        const type = record.version_type || "standard";
        const allowed = versionData[`${type}_allowed`] || [];
        const latest = versionData[`${type}_latest`];

        return allowed.includes(version) ? (
          <Tag color="green">Stable {version === latest ? "(Latest)" : ""}</Tag>
        ) : (
          <Tag color="volcano">Outdated</Tag>
        );
      },
    },
    {
      title: "Health Status",
      dataIndex: "timestamp",
      width: 160,
      fixed: "right",
      render: (val, record) => {
        if (!val) return <Tag>Inactive</Tag>;

        const diff = dayjs().diff(dayjs(val), "days");
        const version = record.myzen_version?.split(" ")[0];
        const allowed =
          versionData[`${record.version_type}_allowed`]?.includes(version);

        if (diff <= 2 && allowed) return <Tag color="green">Healthy</Tag>;
        if (diff <= 7) return <Tag color="gold">Concerning</Tag>;
        return <Tag>Inactive</Tag>;
      },
    },
  ];

  return (
    <ConfigProvider>
      <Table
        rowKey="key"
        dataSource={devcieData}
        columns={columns}
        size="small"
        scroll={{ x: 1500 }}
        pagination={false}
        expandable={{
          expandedRowKeys,
          expandIcon: () => null,
          indentSize: 0,
        }}
      />
    </ConfigProvider>
  );
};

export default UserDeviceTableComponent;
